import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ArchivedDeals = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadArchivedDeals();
  }, []);

  const loadArchivedDeals = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(response.data || []);
    } catch (error) {
      console.error('Error loading archived deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (deal) => {
    if (!window.confirm(`Restore ${deal.business} to active deals?`)) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${deal.id}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: `${deal.business} restored` });
      loadArchivedDeals();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to restore deal', variant: 'destructive' });
    }
  };

  const filteredDeals = deals.filter(d => 
    d.business?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.deal_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading archived deals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Archived Deals</h1>
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search archived deals"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>
      </div>

      {filteredDeals.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No archived deals found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Business</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Archived Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Advanced $</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Payback $</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map((deal, idx) => (
                <tr 
                  key={idx} 
                  className="border-b border-gray-100 hover:bg-orange-50 cursor-pointer transition"
                  onClick={() => navigate(`/deals/${deal.display_number ?? deal.id}`)}
                >
                  <td className="px-4 py-3 text-gray-900">{deal.display_number ?? deal.id}</td>
                  <td className="px-4 py-3 text-gray-900 font-medium">{deal.business}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                      {deal.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{deal.type}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {deal.archived_at ? new Date(deal.archived_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-900">${deal.advanced?.toLocaleString() || '0'}</td>
                  <td className="px-4 py-3 text-gray-900">${deal.payback?.toLocaleString() || '0'}</td>
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-xs px-3 py-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(deal);
                      }}
                    >
                      <RefreshCw size={14} className="mr-1" />
                      Restore
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ArchivedDeals;
